import React from 'react';
import {
    BarChart, Bar,
    LineChart, Line,
    PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, Legend,
    ResponsiveContainer,
} from 'recharts';
import { useTheme } from '@mui/material/styles';
import { ChartData } from '../../types/chat';
import EnmaeyaAvatar from '../chat_container/Avatar';
import './ChartMessage.css';

// Cohesive palette: teal accent + warm complementary tones
const COLORS = ['#1D9E98', '#E8850C', '#2F6FB0', '#D9553B', '#7C5CD6', '#12946A'];

// Charts sit on a light "figure" card (like mermaid diagrams), so axis/grid/
// legend/tooltip colors are tuned dark-on-light and work in both app themes.
const PIE_LABEL_FILL = '#25333d';
const TICK_STYLE = { fill: '#4a5c68', fontSize: 12 };
const GRID_COLOR = 'rgba(18,48,63,0.12)';
const TOOLTIP_STYLE = {
    backgroundColor: '#ffffff',
    border: '1px solid rgba(12,32,44,0.15)',
    borderRadius: 8,
    color: '#25333d',
};
const TOOLTIP_LABEL_STYLE = { color: '#12303f', fontWeight: 600 };
const TOOLTIP_ITEM_STYLE = { color: '#25333d' };
const LEGEND_STYLE = { color: '#4a5c68', fontSize: 13 };

interface ChartMessageProps {
    chartData: ChartData;
    isOpen: boolean;
}

const ChartMessage: React.FC<ChartMessageProps> = ({ chartData, isOpen }) => {
    const { type, title, xKey, data } = chartData;
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';
    const textColor = isDark ? '#e6e6e6' : '#25333d';

    // If the specified yKey doesn't exist in the data, fall back to the first
    // numeric key that isn't xKey (handles LLM key-name mismatches).
    const yKey = (() => {
        if (data[0] && chartData.yKey in data[0]) return chartData.yKey;
        return Object.keys(data[0] || {}).find(
            k => k !== xKey && typeof data[0][k] === 'number'
        ) ?? chartData.yKey;
    })();

    const renderChart = () => {
        if (type === 'pie') {
            return (
                <PieChart>
                    <Pie
                        data={data} dataKey={yKey} nameKey={xKey}
                        cx="50%" cy="50%" outerRadius={110}
                        label={{ fill: PIE_LABEL_FILL, fontSize: 12 }}
                    >
                        {data.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
                    <Legend wrapperStyle={LEGEND_STYLE} />
                </PieChart>
            );
        }
        if (type === 'line') {
            return (
                <LineChart data={data}>
                    <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} />
                    <XAxis dataKey={xKey} tick={TICK_STYLE} axisLine={{ stroke: GRID_COLOR }} tickLine={false} />
                    <YAxis tick={TICK_STYLE} axisLine={{ stroke: GRID_COLOR }} tickLine={false} />
                    <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} />
                    <Legend wrapperStyle={LEGEND_STYLE} />
                    <Line type="monotone" dataKey={yKey} stroke={COLORS[0]} strokeWidth={2.5} dot={{ r: 4, fill: COLORS[0] }} />
                </LineChart>
            );
        }
        // default: bar
        return (
            <BarChart data={data}>
                <CartesianGrid strokeDasharray="3 3" stroke={GRID_COLOR} vertical={false} />
                <XAxis dataKey={xKey} tick={TICK_STYLE} axisLine={{ stroke: GRID_COLOR }} tickLine={false} />
                <YAxis tick={TICK_STYLE} axisLine={false} tickLine={false} />
                <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} itemStyle={TOOLTIP_ITEM_STYLE} cursor={{ fill: 'rgba(255,255,255,0.05)' }} />
                <Legend wrapperStyle={LEGEND_STYLE} />
                <Bar dataKey={yKey} radius={[4, 4, 0, 0]}>
                    {data.map((_, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                </Bar>
            </BarChart>
        );
    };

    return (
        <div className="chat-message bot chart-bubble" style={{ color: textColor }}>
            <div className="message-inner">
                <div className="message-head">
                    <span className="message-avatar">
                        <EnmaeyaAvatar status={isOpen ? 'online' : 'offline'} />
                    </span>
                    <span className="message-author">Enmaeya Agent</span>
                </div>
                <div className="chart-message-content">
                    {title && <p className="chart-title">{title}</p>}
                    <ResponsiveContainer width="100%" height={300}>
                        {renderChart()}
                    </ResponsiveContainer>
                </div>
            </div>
        </div>
    );
};

export default ChartMessage;
